<template>
  <div>
    <div class="mb-3">
      <router-link to="/work/buckets" class="btn btn-secondary btn-no-r">
        <span><BaseIcon name="active" /> {{ $t("work.dashboard") }}</span>
      </router-link>
      <router-link
        to="/work/tracking"
        class="btn btn-no-l btn-no-r btn-primary"
      >
        <span><BaseIcon name="timer" /> {{ $t("work.timeTracking") }}</span>
      </router-link>
      <router-link
        to="/work/tracking/performance"
        class="btn btn-no-l btn-secondary"
      >
        <span><BaseIcon name="timer" /> {{ $t("work.timeTracking") }}</span>
      </router-link>
    </div>

    <div class="white-box mt-3" v-for="week in weeks" :key="week">
      <div class="d-flex justify-content-evenly week-header">
        <div
          v-for="day in week"
          :key="day"
          class="text-center time-day"
          :class="{ 'time-today': today === day.date }"
        >
          <div class="text-muted small">{{ day.day }}</div>
          <div class="fw-bold">
            {{ formatDate(day.date) }}
          </div>
          <div class="bold-24">
            {{ formatTime(data.duration[day.date] ?? 0) }}
          </div>
          <div
            class="mt-2 text-start px-2"
            v-if="
              data.userDuration[day.date] &&
              Object.keys(data.userDuration[day.date]).length > 0
            "
          >
            <div
              v-for="(duration, user) in data.userDuration[day.date]"
              :key="user"
              class="small d-flex justify-content-between"
            >
              <span class="text-truncate" style="max-width: 80px"
                >{{ user }}:</span
              >
              <span class="fw-bold ms-1">{{ formatTime(duration) }}</span>
            </div>
          </div>
        </div>
      </div>
      <template v-for="day in Object.values(week).reverse()" :key="day">
        <div
          class="p-3"
          v-if="data.rows[day.date]?.length > 0 || today === day.date"
        >
          <h2 class="semibold-16 mb-3">
            {{ day.day }} {{ formatDate(day.date) }}
          </h2>
          <div
            v-for="row in data.rows[day.date]"
            :key="row"
            class="tracked-session mb-3"
            :class="{
              assigned: row.type === 'task',
              unassigned: row.type === 'bucket',
            }"
          >
            <div class="d-flex justify-content-between align-items-center">
              <div>
                <div class="session-title">
                  <span class="badge badge-info me-2">{{ row.user_name }}</span>
                  {{ row.bucket }}
                </div>
                <div class="session-description small text-muted">
                  {{ row.name ?? "Automatic tracking" }}
                </div>
                <div class="session-meta mt-1">
                  <span class="text-muted me-3">
                    {{ timeRange(row) }}
                  </span>
                  <div class="session-tag" v-if="row.group_name">
                    {{ row.group_name }}
                  </div>
                </div>
              </div>
              <div class="text-end">
                <div class="bold-19">
                  {{ formatTime(row.duration) }}
                </div>
              </div>
            </div>
          </div>
        </div>
      </template>
    </div>
  </div>
</template>

<script>
import http from "@/modules/http";
import date from "../../../modules/date";
import BaseIcon from "../../../components/icons/BaseIcon.vue";

export default {
  name: "Timing",
  components: { BaseIcon },
  data() {
    return {
      data: undefined,
    };
  },
  computed: {
    today() {
      const d = new Date();
      const year = d.getFullYear();
      const month = String(d.getMonth() + 1).padStart(2, "0");
      const day = String(d.getDate()).padStart(2, "0");

      return `${year}-${month}-${day}`;
    },
    weeks() {
      if (this.data === undefined) {
        return 0;
      }
      let keys = Object.keys(this.data.duration);
      let firstMonday = this.getNextMonday(keys[0]);
      let start = keys.findIndex((e) => e === firstMonday);
      let weeks = this.getWeeksBetweenDates(firstMonday, keys[keys.length - 1]);
      return Array.from({ length: weeks }, (_, index) =>
        this.getWeekDatesWithDayNames(keys[start + index * 7])
      ).reverse();
    },
  },
  mounted() {
    this.load();
  },
  methods: {
    load() {
      http.fetch("/work/times/all").then((data) => {
        this.data = data;
      });
    },
    timeRange(row) {
      const start = new Date(row.started_at);
      const end = new Date(start.getTime() + row.duration * 1000);

      const format = (d) => d.toTimeString().slice(0, 5);

      return `${format(start)} - ${format(end)}`;
    },
    formatDate(val) {
      return date.format(val, false, false);
    },
    formatTime(seconds, secunds = false) {
      if (seconds >= 60) {
        const hours = Math.floor(seconds / 3600);
        const minutes = Math.floor((seconds % 3600) / 60);
        if (secunds) {
          const remainingSeconds = seconds % 60;
          return `${hours}:${minutes
            .toString()
            .padStart(2, "0")}:${remainingSeconds.toString().padStart(2, "0")}`;
        }
        return `${hours}:${minutes.toString().padStart(2, "0")}`;
      } else {
        return `${seconds} s`;
      }
    },
    getNextMonday(startDate) {
      const d = new Date(startDate);
      while (d.getDay() !== 1) {
        d.setDate(d.getDate() + 1);
      }

      const year = d.getFullYear();
      const month = String(d.getMonth() + 1).padStart(2, "0");
      const day = String(d.getDate()).padStart(2, "0");
      return `${year}-${month}-${day}`;
    },
    getWeeksBetweenDates(startDate, endDate) {
      const d = new Date(startDate);
      const date2 = new Date(endDate);

      d.setHours(0);
      d.setMinutes(0);
      d.setSeconds(0);
      date2.setHours(23);
      date2.setMinutes(59);
      date2.setSeconds(59);

      const oneDay = 24 * 60 * 60 * 1000;
      const diffDays = Math.round(
        Math.abs((d.getTime() - date2.getTime()) / oneDay)
      );
      return Math.ceil(diffDays / 7);
    },
    getWeekDatesWithDayNames(startDate) {
      const dates = [];
      const currentDate = new Date(startDate);

      const firstDay = new Date(
        currentDate.setDate(
          currentDate.getDate() - ((currentDate.getDay() + 6) % 7)
        )
      );

      const lastDay = new Date(firstDay);
      lastDay.setDate(firstDay.getDate() + 6);

      const dayNames = [
        "Sunday",
        "Monday",
        "Tuesday",
        "Wednesday",
        "Thursday",
        "Friday",
        "Saturday",
      ];

      for (
        let d = new Date(firstDay);
        d <= lastDay;
        d.setDate(d.getDate() + 1)
      ) {
        const dayIndex = d.getDay();

        const year = d.getFullYear();
        const month = String(d.getMonth() + 1).padStart(2, "0");
        const day = String(d.getDate()).padStart(2, "0");

        dates.push({
          day: dayNames[dayIndex],
          date: `${year}-${month}-${day}`,
        });
      }

      return dates;
    },
  },
};
</script>
<style scoped>
.week-header {
  padding: 20px;
  background: linear-gradient(135deg, #f8fafc, #eef3fb);
  border-bottom: 1px solid #e7edf7;
}

.time-day {
  padding: 0.6em 1.2em;
  margin: 0.3em;
  border-radius: 6px;
}
.time-today {
  color: #62277d;
  border: 1px solid #a947d6;
}
.time-today .text-muted {
  color: #7d578e !important;
}

.week-totals div {
  flex: 1;
  padding: 6px;
}

.week-totals .value {
  font-weight: 600;
  font-size: 15px;
}

.week-totals .label {
  font-size: 12px;
  color: #6b7280;
}

/* Session list - modern timeline feel */
.tracked-session {
  padding: 14px 18px;
  border-radius: 16px;
  background: #f8fafc;
  border: 1px solid #e7edf7;
  transition: all 0.25s ease;
  position: relative;
  margin-bottom: 10px;
}

.tracked-session:hover {
  transform: translateY(-2px) scale(1.01);
  background: #ffffff;
  box-shadow: 0 10px 30px rgba(15, 23, 42, 0.06);
}

.tracked-session.assigned {
  border-left: 4px solid #22c55e;
}

.tracked-session.unassigned {
  border-left: 4px solid #f43f5e;
}

.tracked-session strong {
  font-size: 15px;
}

.text-muted {
  color: #94a3b8 !important;
}
.session-title {
  font-weight: 600;
  font-size: 15px;
}

.session-meta {
  display: flex;
  justify-content: space-between;
  font-size: 12px;
  margin-top: 6px;
  color: #64748b;
}

.session-tags {
  margin-top: 8px;
}

.session-tag {
  display: inline-block;
  font-size: 11px;
  padding: 4px 10px;
  border-radius: 10px;
  background: #eef2ff;
  color: #4f46e5;
  margin-right: 6px;
}
</style>
